import { ESdChangeReason, EWFNodeType, IConditionModel, IWFDecision } from "@kuika/kuika-cl-model";
import { ConditionSetType } from "@kuika/kuika-cl-model/src/models/builder.dtos";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import { WorkFlowDecisionProperties } from "./workflow-decision-properties";
import "./workflow-exclusive-gateway-properties.scss";

export interface IWorkflowExclusiveGatewayPropertiesState {
  defaultPath: IWFDecision | undefined;
  decisionArrows: IWFDecision[] | undefined;
}

export interface IWorkflowExclusiveGatewayPropertiesProps {}

class WorkflowExclusiveGatewayProperties extends PureSdComponent<
  IWorkflowExclusiveGatewayPropertiesProps & IWithSDProps & IWithContextProp,
  IWorkflowExclusiveGatewayPropertiesState
> {
  constructor(props: IWorkflowExclusiveGatewayPropertiesProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      defaultPath: undefined,
      decisionArrows: undefined
    };
  }

  prepareDecisionArrows = () => {
    const wf = this.props.sd.selectedWf;
    let decisionArrows = wf
      ? (this.props.sd
          .findAllNodesRecursively(
            wf,
            EWFNodeType.Arrow,
            (node) => (node as IWFDecision).fromNodeId === this.props.sd.selectedWfNode?.id
          )
          .sort((a, b) => (a as IWFDecision).internalIndex - (b as IWFDecision).internalIndex) as IWFDecision[])
      : [];

    if (wf && decisionArrows && decisionArrows.length > 0) {
      let defaultPath = decisionArrows.find((a) => a.isDefault == true);
      if (defaultPath === undefined) {
        defaultPath = decisionArrows[decisionArrows.length - 1];
        defaultPath.isDefault = true;
      } else {
        // place default item at last
        var defaultIndex = decisionArrows.findIndex((d) => d.isDefault == true);
        decisionArrows = this.reorder(decisionArrows, defaultIndex, decisionArrows.length - 1);
      }
      this.setInternalIndexes(decisionArrows);
      // this.props.sd.setDefaultArrow(defaultPath.id);
      if (decisionArrows) this.updateContextData(decisionArrows);
      this.setState({ defaultPath, decisionArrows });
    }
  };

  componentDidMount() {
    this.prepareDecisionArrows();
  }

  componentDidUpdate(prevProps: IWorkflowExclusiveGatewayPropertiesProps & IWithSDProps & IWithContextProp) {
    if (
      prevProps?.sd.selectedWfNode?.id !== this.props.sd.selectedWfNode?.id ||
      prevProps.sd.selectedWf?.id !== this.props.sd.selectedWf?.id
    ) {
      this.prepareDecisionArrows();
    }
  }

  handleDefaultPathChange = (value) => {
    var newDefaultIndex = this.state.decisionArrows!.findIndex((d) => d.id == value);
    const orderedArrows: IWFDecision[] = this.reorder(
      this.state.decisionArrows,
      newDefaultIndex,
      this.state.decisionArrows!.length - 1
    );
    this.setInternalIndexes(orderedArrows);
    const defaultPath = orderedArrows[orderedArrows.length - 1];
    this.updateContextData(orderedArrows);
    this.setState({ defaultPath, decisionArrows: orderedArrows });
  };

  onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const decisionArrows = this.reorder(this.state.decisionArrows, result.source.index, result.destination.index);
    this.setState({ decisionArrows });
    this.updateContextData(decisionArrows);
  };

  getDefaultPart = (decisionArrows) => {
    const { defaultPath } = this.state;
    return (
      <div
        className="kuika_blue_panel kuika-wf-exclusive-gateway__default-panel"
        style={{ display: defaultPath ? "block" : "none" }}
      >
        <KuikaText className="kuika-wf-exclusive-gateway__default-caption">Will execute otherwise</KuikaText>
        <div className="kuika-wf-exclusive-gateway__default-field">
          <KuikaSelectbox
            placeholder="Default Path"
            value={defaultPath?.id}
            onChange={(value: string) => this.handleDefaultPathChange(value)}
            options={decisionArrows.map((decisionArrow: IWFDecision): KuikaSelectboxOption => ({
              key: decisionArrow.id,
              value: decisionArrow.id,
              label: decisionArrow.name
            }))}
          />
        </div>
      </div>
    );
  };

  // Helpers
  reorder = (list, startIndex, endIndex) => {
    const result = _.cloneDeep(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    this.setInternalIndexes(result);
    return result;
  };

  setInternalIndexes = (list) => {
    for (let i = 0; i < list.length; i++) {
      list[i].internalIndex = i;
      list[i].isDefault = i == list.length - 1;
    }
  };

  updateContextData = (orderDecisions: IWFDecision[]) => {
    const wf = this.props.sd.selectedWf;
    if (wf) {
      let defaultArrowId: string | undefined;
      orderDecisions.map((o) => {
        const decision = this.props.sd.findNodeRecursively(o.id, EWFNodeType.Arrow, wf) as IWFDecision | undefined;
        if (decision) {
          decision.internalIndex = o.internalIndex;
          decision.isDefault = o.isDefault;
          if (o.isDefault) {
            defaultArrowId = o.id;
          }
        }
      });
      if (defaultArrowId) {
        const gw = this.props.sd.selectedWfNode;
        if (gw?.id) {
          this.props.sd.updateDiagramElement(gw.id, [
            { name: "default", value: defaultArrowId, isValueIdToGetElement: true }
          ]);
        }
      }
      this.props.sd.saveWorkFlow(wf, ESdChangeReason.SaveWorkflow);
    }
  };
  //

  render() {
    const { defaultPath, decisionArrows } = this.state;
    if (decisionArrows === undefined) {
      return <></>;
    }

    const decisionProperties = decisionArrows.map((decisionArrow, index, array) => {
      decisionArrow.condition;
      if (decisionArrow.condition === undefined) {
        decisionArrow.condition = {
          conditionSet: { conditions: [], type: ConditionSetType.and },
          id: Guid.create().toString(),
          isOrGroup: false
        } as IConditionModel;
      }

      return (
        <div style={{ display: decisionArrow.id === defaultPath?.id ? "none" : "block" }}>
          {/* style={{ display: decisionArrow.id === defaultPath?.id ? "none" : "block" }} */}
          <Draggable key={decisionArrow.id} draggableId={decisionArrow.id} index={index}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                // style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
              >
                <WorkFlowDecisionProperties decisionId={decisionArrow.id}></WorkFlowDecisionProperties>
              </div>
            )}
          </Draggable>
        </div>
      );
    });
    return (
      <div>
        <DragDropContext onDragEnd={this.onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {decisionProperties}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {this.getDefaultPart(decisionArrows)}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (changeReason === ESdChangeReason.SaveWorkflow || changeReason === ESdChangeReason.WFStepMappingChange) {
      return true;
    }
    return false;
  };
}

const workflowExclusiveGatewayProperties = withContext<IWorkflowExclusiveGatewayPropertiesProps>(
  withSD<IWorkflowExclusiveGatewayPropertiesProps>(WorkflowExclusiveGatewayProperties)
);
export { workflowExclusiveGatewayProperties as WorkflowExclusiveGatewayProperties };
